import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ClipboardCheck } from "lucide-react";
import LeadCaptureForm, { LEAD_CAPTURED_KEY } from "./LeadCaptureForm";

const DISMISS_KEY = "lead_popup_dismissed_at";
const DISMISS_DAYS = 7;
const DELAY_MS = 20000;

function shouldShow() {
  if (localStorage.getItem(LEAD_CAPTURED_KEY)) return false;
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return !ts || Date.now() - ts > DISMISS_DAYS * 86400000;
}

export default function LeadMagnetPopup({ enabled = true }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !shouldShow()) return undefined;
    let fired = false;
    const trigger = () => {
      if (fired || !shouldShow()) return;
      fired = true;
      setOpen(true);
    };
    const timer = setTimeout(trigger, DELAY_MS);
    const onLeave = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener("mouseleave", onLeave);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [enabled]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-popup"
          role="dialog"
          aria-label="Free Texas LLC checklist"
          initial={{ opacity: 0, x: 40, y: 20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 40, y: 20 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed inset-x-3 bottom-24 z-[70] rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[6px_6px_0px_var(--c-shadow)] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[400px]"
        >
          <button
            data-testid="lead-popup-close"
            onClick={dismiss}
            aria-label="Close"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text)] transition-colors hover:bg-[var(--c-hover-bg)]"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#C8102E] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white">
            <ClipboardCheck className="h-3.5 w-3.5" /> Free checklist
          </p>
          <h3 className="mt-3 font-heading text-xl font-black leading-tight text-[var(--c-text)]">
            Leaving already? Take the Texas LLC checklist with you.
          </h3>
          <p className="mt-1.5 text-sm text-[var(--c-muted)]">All 9 steps and 43 tasks on 2 printable pages — free.</p>
          <div className="mt-4">
            <LeadCaptureForm source="exit_popup" compact />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
